import React, { useEffect, useState } from "react";
import analytics from "web/script/analytics/analytics";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import CookieSettingsOption from "web/react/components/cookie-settings-option/cookie-settings-option";
import { useDispatch } from "react-redux";
import store from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import {
    consentToAllCookies,
    CookieConsentLevel,
    rejectAllNonEssentialCookies,
    setCookieConsent,
} from "web/redux/ducks/cookie-consent";
import classnames from "classnames";
import styles from "./cookie-settings.css";

export interface CookieSettingsProps {
    isOpen: boolean;
    onModalClose: () => void;
}

interface SelectedState {
    customizationCookie: boolean;
    adCookie: boolean;
    analyticalCookie: boolean;
}

function CookieSettings({ isOpen, onModalClose }: CookieSettingsProps): JSX.Element {
    const dispatch = useDispatch();

    const reducerState = store.getState().cookieConsentReducer;
    const initialSelectedState: SelectedState = {
        customizationCookie: reducerState.acceptsCustomization,
        adCookie: reducerState.acceptsAds,
        analyticalCookie: reducerState.acceptsAnalytics,
    };

    const [formHasChanged, setFormHasChanged] = useState(false);
    const customizationCookieOptionID = "customization-cookie-option";
    const adCookieOptionID = "ad-cookie-option";
    const analyticalCookieOptionID = "analytical-cookie-option";

    function getCurrentSelectedState(): SelectedState {
        const customizationCookieOptionElement = document.getElementById(
            customizationCookieOptionID
        );
        const adCookieOptionElement = document.getElementById(adCookieOptionID);
        const analyticalCookieOptionElement = document.getElementById(analyticalCookieOptionID);

        return {
            customizationCookie: customizationCookieOptionElement
                ? customizationCookieOptionElement.getAttribute("checked") === "true"
                : false,
            adCookie: adCookieOptionElement
                ? adCookieOptionElement.getAttribute("checked") === "true"
                : false,
            analyticalCookie: analyticalCookieOptionElement
                ? analyticalCookieOptionElement.getAttribute("checked") === "true"
                : false,
        };
    }

    function onClose(): void {
        setFormHasChanged(false);
        onModalClose();
    }

    function acceptAllOnClick(): void {
        // copied from cookie-banner.jsx - so we know what % of users accept all cookies
        analytics.event("Cookie consent", "Clicked", "Hide");

        dispatch(consentToAllCookies());
        onClose();
    }

    function acceptRequiredOnClick(): void {
        analytics.event("cookie_settings", "clicked", "accept_required");

        dispatch(rejectAllNonEssentialCookies());
        onClose();
    }

    function saveChangesOnClick(): void {
        analytics.event("cookie_settings", "clicked", "save_changes");

        function getConsentLevel(accepts: boolean): CookieConsentLevel {
            return accepts ? CookieConsentLevel.Accepts : CookieConsentLevel.Rejects;
        }

        const currentSelectedState = getCurrentSelectedState();
        const cookieConsent = {
            customizationCookie: getConsentLevel(currentSelectedState.customizationCookie),
            adCookie: getConsentLevel(currentSelectedState.adCookie),
            analyticsCookie: getConsentLevel(currentSelectedState.analyticalCookie),
        };

        dispatch(setCookieConsent(cookieConsent));

        onClose();
    }

    function discardChangesOnClick(): void {
        onClose();
    }

    const acceptAllButtonProps = {
        text: gettext("cookie_notification.cookie_settings.accept_all.cta"),
        onClick: acceptAllOnClick,
    };
    const acceptRequiredLinkProps = {
        text: gettext("cookie_notification.cookie_settings.accept_required.link"),
        onClick: acceptRequiredOnClick,
    };
    const discardChangesLinkProps = {
        text: gettext("cookie_notification.cookie_settings.discard_changes.link"),
        onClick: discardChangesOnClick,
    };
    const saveChangesButtonProps = {
        text: gettext("cookie_notification.cookie_settings.save_changes.cta"),
        onClick: saveChangesOnClick,
    };

    function onSelectedChange(): void {
        const currentSelectedState = getCurrentSelectedState();

        if (
            currentSelectedState.customizationCookie !== initialSelectedState.customizationCookie ||
            currentSelectedState.adCookie !== initialSelectedState.adCookie ||
            currentSelectedState.analyticalCookie !== initialSelectedState.analyticalCookie
        ) {
            setFormHasChanged(true);
        } else {
            setFormHasChanged(false);
        }
    }

    function finishedTextTransition(): void {
        const finishedLinkElement = document.getElementById("finished-link");
        const finishedButtonElement = document.getElementById("finished-button");

        if (finishedLinkElement && finishedButtonElement) {
            finishedLinkElement.classList.toggle(styles.fadeIn);
            finishedLinkElement.classList.toggle(styles.fadeOut);

            finishedButtonElement.classList.toggle(styles.fadeIn);
            finishedButtonElement.classList.toggle(styles.fadeOut);
        }
    }

    // the finished action items can be one of two different propositions - 'accept' or 'save'
    const [finishedButtonProps, setFinishedButtonProps] = useState(acceptAllButtonProps);
    const [finishedLinkProps, setFinishedLinkProps] = useState(acceptRequiredLinkProps);

    useEffect(() => {
        // fade out old text
        finishedTextTransition();

        // change to new text (giving time for the old text to fade out)
        setTimeout(() => {
            if (formHasChanged) {
                setFinishedButtonProps(saveChangesButtonProps);
                setFinishedLinkProps(discardChangesLinkProps);
            } else {
                setFinishedButtonProps(acceptAllButtonProps);
                setFinishedLinkProps(acceptRequiredLinkProps);
            }
        }, 150);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formHasChanged]);

    useEffect(() => {
        // fade in new text
        finishedTextTransition();
    }, [finishedButtonProps]);

    return (
        <GenericModal isOpen={isOpen} onClose={onClose} className={styles.modal} noScroll={false}>
            <div className={styles.content}>
                <div className={styles.title}>
                    {// "Cookie Settings"
                    gettext("cookie_notification.cookie_settings.title")}
                </div>
                <div className={styles.options}>
                    <CookieSettingsOption
                        optionID={"required-cookies"}
                        optionTitle={
                            // Required Cookies
                            gettext("cookie_notification.required_cookies.title")
                        }
                        isEssential={true}
                        cookieDescription={gettext(
                            "cookie_notification.required_cookies.description"
                        )}
                    />
                    <CookieSettingsOption
                        optionID={customizationCookieOptionID}
                        optionTitle={
                            // "Site Customization"
                            gettext("cookie_notification.site_customization.title")
                        }
                        isEssential={false}
                        cookieDescription={gettext(
                            "cookie_notification.site_customization.description"
                        )}
                        isSelected={initialSelectedState.customizationCookie}
                        onChange={onSelectedChange}
                    />
                    <CookieSettingsOption
                        optionID={analyticalCookieOptionID}
                        optionTitle={
                            // "Analytical / Performance"
                            gettext("cookie_notification.analytical_performance.title")
                        }
                        isEssential={false}
                        cookieDescription={gettext(
                            "cookie_notification.analytical_performance.description"
                        )}
                        isSelected={initialSelectedState.analyticalCookie}
                        onChange={onSelectedChange}
                    />
                    <CookieSettingsOption
                        optionID={adCookieOptionID}
                        optionTitle={
                            // Personalized Advertising
                            gettext("cookie_notification.personalised_advertising.title")
                        }
                        isEssential={false}
                        cookieDescription={gettext(
                            "cookie_notification.personalised_advertising.description"
                        )}
                        isSelected={initialSelectedState.adCookie}
                        onChange={onSelectedChange}
                    />
                </div>
                <div className={styles.finishedLink}>
                    <a
                        id={"finished-link"}
                        className={classnames(styles.link, styles.fadeIn)}
                        onClick={finishedLinkProps.onClick}
                    >
                        {finishedLinkProps.text}
                    </a>
                </div>
                <PrimaryButton
                    className={""}
                    fullHeight={false}
                    fullWidth={true}
                    openInNewTab={false}
                    disabled={false}
                    onClick={finishedButtonProps.onClick}
                >
                    <span id={"finished-button"} className={styles.fadeIn}>
                        {finishedButtonProps.text}
                    </span>
                </PrimaryButton>
            </div>
        </GenericModal>
    );
}

export default CookieSettings;
